"use client"

import * as React from "react"
import {
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { Inbox, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import type { Column } from "@tanstack/react-table"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onRowClick?: (row: TData) => void
  pageSize?: number
  className?: string
  noBorder?: boolean
  enableRowSelection?: boolean
  onSelectionChange?: (selectedRows: TData[]) => void
  emptyIcon?: React.ReactNode
  emptyTitle?: string
  emptyDescription?: string
  loading?: boolean
  // Server-side pagination props
  manualPagination?: boolean
  manualSorting?: boolean
  totalCount?: number
  pageIndex?: number
  onPaginationChange?: (pageIndex: number, pageSize: number) => void
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
}

// Sortable header component matching admin panel style
interface SortableHeaderProps<TData, TValue> {
  column: Column<TData, TValue>
  title: string
  className?: string
}

export function SortableHeader<TData, TValue>({
  column,
  title,
  className,
}: SortableHeaderProps<TData, TValue>) {
  const sorted = column.getIsSorted()

  return (
    <button
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className={cn(
        "flex items-center hover:text-foreground transition-colors",
        className
      )}
    >
      {title}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 h-3 w-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />
      )}
    </button>
  )
}

// Helper to create a select column
export function getSelectColumn<TData>(): ColumnDef<TData> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  }
}

// Generate page numbers to display
function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = []

  if (totalPages <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(1)

    if (currentPage > 3) {
      pages.push('ellipsis')
    }

    // Show pages around current
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis')
    }

    // Always show last page
    pages.push(totalPages)
  }

  return pages
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  pageSize = 10,
  className,
  noBorder = false,
  enableRowSelection = false,
  onSelectionChange,
  emptyIcon,
  emptyTitle = "No results",
  emptyDescription,
  loading = false,
  // Server-side pagination props
  manualPagination = false,
  manualSorting = false,
  totalCount,
  pageIndex: controlledPageIndex,
  onPaginationChange,
  sorting: controlledSorting,
  onSortingChange,
}: DataTableProps<TData, TValue>) {
  // Internal state for client-side mode
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  })

  // Use controlled state for server-side mode, internal state for client-side
  const sorting = manualSorting && controlledSorting !== undefined ? controlledSorting : internalSorting
  const pagination = manualPagination && controlledPageIndex !== undefined
    ? { pageIndex: controlledPageIndex, pageSize }
    : internalPagination

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater
    if (manualSorting && onSortingChange) {
      onSortingChange(newSorting)
    } else {
      setInternalSorting(newSorting)
    }
  }

  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    if (manualPagination && onPaginationChange) {
      onPaginationChange(newPagination.pageIndex, newPagination.pageSize)
    } else {
      setInternalPagination(newPagination)
    }
  }

  // Calculate page count for server-side pagination
  const pageCount = manualPagination && totalCount !== undefined
    ? Math.ceil(totalCount / pageSize)
    : undefined

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Only use client-side models when not in manual mode
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(manualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      rowSelection,
      pagination,
    },
    // Server-side pagination config
    manualPagination,
    manualSorting,
    pageCount,
    enableRowSelection,
  })

  // Call onSelectionChange when selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original)
      onSelectionChange(selectedRows)
    }
  }, [rowSelection, onSelectionChange, table])

  // Reset selection when data changes
  React.useEffect(() => {
    setRowSelection((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return {};
    });
  }, [data])

  const currentPage = pagination.pageIndex + 1
  const totalPages = manualPagination && pageCount ? pageCount : table.getPageCount()
  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const canPreviousPage = manualPagination ? pagination.pageIndex > 0 : table.getCanPreviousPage()
  const canNextPage = manualPagination ? pagination.pageIndex < totalPages - 1 : table.getCanNextPage()

  return (
    <div className={cn(className)}>
      <div className={cn(
        "overflow-hidden",
        !noBorder && "rounded-md border"
      )}>
        <Table>
          <TableHeader className="bg-muted/40">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header, index) => {
                  const isLast = index === headerGroup.headers.length - 1
                  const isFirst = index === 0
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "text-sm font-medium text-muted-foreground",
                        isFirst && (noBorder ? "pl-6" : "pl-4"),
                        isLast && (noBorder ? "pr-6 text-right" : "pr-4 text-right"),
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className="border-b hover:bg-transparent">
                  {columns.map((col, colIndex) => {
                    const isFirst = colIndex === 0
                    const isLast = colIndex === columns.length - 1
                    const isSelectCol = 'id' in col && col.id === 'select'
                    // Vary widths per row for a natural look
                    const widths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5']
                    const width = isSelectCol ? 'w-4' : widths[(rowIndex + colIndex) % widths.length]
                    return (
                      <TableCell
                        key={colIndex}
                        className={cn(
                          isFirst && (noBorder ? "pl-6" : "pl-4"),
                          isLast && (noBorder ? "pr-6" : "pr-4"),
                        )}
                      >
                        <Skeleton className={cn(isSelectCol ? "h-4 rounded-sm" : "h-4", width, isLast && "ml-auto")} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    "group transition-colors border-b",
                    onRowClick && "cursor-pointer hover:bg-muted/30",
                    row.getIsSelected() && "bg-blue-50 dark:bg-blue-950/30",
                  )}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isLast = index === row.getVisibleCells().length - 1
                    const isFirst = index === 0
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          isFirst && (noBorder ? "pl-6" : "pl-4"),
                          isLast && (noBorder ? "pr-6 text-right" : "pr-4 text-right"),
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent border-b">
                <TableCell
                  colSpan={columns.length}
                  className={cn("py-16", noBorder ? "pl-6 pr-6" : "pl-4 pr-4")}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      {emptyIcon || <Inbox className="h-6 w-6 text-muted-foreground" />}
                    </div>
                    <h3 className="text-sm font-medium mb-1">{emptyTitle}</h3>
                    {emptyDescription && (
                      <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                        {emptyDescription}
                      </p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className={cn(
          "flex items-center justify-between py-4",
          noBorder && "px-6"
        )}>
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
            {manualPagination && totalCount !== undefined && (
              <> ({totalCount} total)</>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (manualPagination && onPaginationChange) {
                  onPaginationChange(pagination.pageIndex - 1, pageSize)
                } else {
                  table.previousPage()
                }
              }}
              disabled={!canPreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {pageNumbers.map((page, i) => (
              page === 'ellipsis' ? (
                <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">...</span>
              ) : (
                <Button
                  key={page}
                  variant={page === currentPage ? "default" : "outline"}
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    if (manualPagination && onPaginationChange) {
                      onPaginationChange(page - 1, pageSize)
                    } else {
                      table.setPageIndex(page - 1)
                    }
                  }}
                >
                  {page}
                </Button>
              )
            ))}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (manualPagination && onPaginationChange) {
                  onPaginationChange(pagination.pageIndex + 1, pageSize)
                } else {
                  table.nextPage()
                }
              }}
              disabled={!canNextPage}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
